import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "@/routes";
import { addDays } from "date-fns";
import moment from "moment";
import { 
  Calendar as CalendarIcon, 
  Clock, 
  Video, 
  ChevronLeft, 
  ChevronRight,
  Check,
  Star,
  Loader2,
  User
} from "lucide-react";
import { MemberSidebar } from "@/components/layout/MemberSidebar";
import { MemberHeader } from "@/components/layout/MemberHeader";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Calendar } from "@/components/ui/calendar";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { generateTimeSlotsUTC, toUTCDate, toUTCTime, toUTCDateFromLocalTime } from "@/lib/timeUtils";

interface ProviderAvailabilitySlot {
  day: number;
  start_hour: number;
  end_hour: number;
}

interface Provider {
  id: string;
  user_id: string;
  display_name: string;
  specialty: string;
  bio: string | null;
  avatar_url: string | null;
  rating: number;
  review_count: number;
  availability: ProviderAvailabilitySlot[];
}

// Time slot generation now handled by generateTimeSlotsUTC from timeUtils

const visitReasons = [
  "General Check-up",
  "Follow-up Visit",
  "Medication Review",
  "New Symptoms",
  "Chronic Condition Management",
  "Mental Health",
  "Other",
];

export default function MemberScheduling() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [selectedProvider, setSelectedProvider] = useState<string | null>(null);
  const [selectedTime, setSelectedTime] = useState<{ display: string; value: string; utcDate: string } | null>(null);
  const [visitReason, setVisitReason] = useState<string>("");
  const [notes, setNotes] = useState("");
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [providers, setProviders] = useState<Provider[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isBooking, setIsBooking] = useState(false);
  const [bookedTimes, setBookedTimes] = useState<string[]>([]);

  // Fetch providers with their availability
  useEffect(() => {
    const fetchProviders = async () => {
      setIsLoading(true);
      const { data, error } = await supabase
        .from("providers")
        .select("*")
        .eq("is_accepting_patients", true);

      if (error) {
        console.error("Error fetching providers:", error);
        toast.error("Failed to load providers");
        setIsLoading(false);
        return;
      }

      // Fetch availability for all providers
      const providerIds = (data || []).map(p => p.id);
      let availabilityMap: Record<string, ProviderAvailabilitySlot[]> = {};
      if (providerIds.length > 0) {
        const { data: avail } = await (supabase as any)
          .from("provider_availability")
          .select("provider_id, day, start_hour, end_hour")
          .in("provider_id", providerIds);
        for (const slot of (avail || [])) {
          if (!availabilityMap[slot.provider_id]) availabilityMap[slot.provider_id] = [];
          availabilityMap[slot.provider_id].push({ day: slot.day, start_hour: slot.start_hour, end_hour: slot.end_hour });
        }
      }

      // Default Mon-Fri 9-17 fallback if provider has no availability rows
      const defaultAvailability: ProviderAvailabilitySlot[] = [1,2,3,4,5].map(day => ({ day, start_hour: 9, end_hour: 17 }));

      setProviders((data || []).map(p => ({
        ...p,
        availability: (availabilityMap[p.id] && availabilityMap[p.id].length > 0)
          ? availabilityMap[p.id]
          : defaultAvailability,
      })));
      setIsLoading(false);
    };

    fetchProviders();
  }, []);

  const selectedProviderData = providers.find((p) => p.id === selectedProvider);

  // Fetch booked times (stored as UTC) for selected provider and date
  useEffect(() => {
    const fetchBookedTimes = async () => {
      if (!selectedProviderData || !selectedDate) {
        setBookedTimes([]);
        return;
      }
      // Query using UTC date derived from the local selected date
      const utcDateStr = toUTCDate(selectedDate);
      const { data } = await supabase
        .from("telehealth_sessions")
        .select("scheduled_time")
        .eq("provider_id", selectedProviderData.user_id)
        .eq("scheduled_date", utcDateStr)
        .in("status", ["scheduled", "waiting", "in_progress"]);

      setBookedTimes((data || []).map((d) => d.scheduled_time));
    };
    fetchBookedTimes();
  }, [selectedProviderData, selectedDate]);

  // Find availability for the selected day of week
  const selectedDayOfWeek = selectedDate.getDay();
  const dayAvailability = selectedProviderData?.availability.find(a => a.day === selectedDayOfWeek);

  const timeSlots = selectedProviderData && dayAvailability
    ? generateTimeSlotsUTC(dayAvailability.start_hour, dayAvailability.end_hour, selectedDate, bookedTimes)
    : [];

  const handleBookAppointment = async () => {
    if (!user || !selectedProviderData || !selectedTime) {
      toast.error("Missing required information");
      return;
    }

    setIsBooking(true);

    try {
      // selectedTime.value is already UTC time, selectedTime.utcDate is the UTC date
      const { error } = await supabase
        .from("telehealth_sessions")
        .insert({
          member_id: user.id,
          provider_id: selectedProviderData.user_id,
          scheduled_date: selectedTime.utcDate,
          scheduled_time: selectedTime.value,
          visit_type: "telehealth",
          visit_reason: visitReason,
          notes: notes || null,
          status: "scheduled",
        })
        .select()
        .single();

      if (error) throw error;

      // Auto-assign provider-member relationship
      await supabase.from("provider_members" as any).upsert(
        { provider_id: selectedProviderData.user_id, member_id: user.id },
        { onConflict: "provider_id,member_id" }
      );

      setShowConfirmDialog(false);
      toast.success("Appointment booked successfully!", {
        description: `Your telehealth session with ${selectedProviderData.display_name} is scheduled for ${moment(selectedDate).format("MMMM D, YYYY")} at ${selectedTime.display}.`,
      });
      navigate(ROUTES.MEMBER);
    } catch (error) {
      console.error("Error booking appointment:", error);
      toast.error("Failed to book appointment. Please try again.");
    } finally {
      setIsBooking(false);
    }
  };

  const canProceedToStep2 = selectedProvider !== null;
  const canProceedToStep3 = selectedDate !== null && selectedTime !== null;
  const canBook = visitReason !== "";

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <MemberSidebar />
        <div className="ml-64">
          <MemberHeader />
          <main className="p-6 flex items-center justify-center min-h-[60vh]">
            <div className="text-center">
              <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
              <p className="mt-2 text-muted-foreground">Loading providers...</p>
            </div>
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <MemberSidebar />
      <div className="ml-64">
        <MemberHeader />
        <main className="p-6">
          {/* Page Header */}
          <div className="mb-8">
            <h1 className="font-display text-3xl font-bold text-foreground">Schedule Telehealth Visit</h1>
            <p className="mt-1 text-muted-foreground">
              Book a video appointment with one of our healthcare providers
            </p>
          </div>

          {/* Progress Steps */}
          <div className="mb-8">
            <div className="flex items-center justify-center gap-4">
              {[1, 2, 3].map((s) => (
                <div key={s} className="flex items-center">
                  <div
                    className={cn(
                      "flex h-10 w-10 items-center justify-center rounded-full border-2 font-semibold transition-colors",
                      step >= s
                        ? "border-primary bg-primary text-primary-foreground"
                        : "border-muted-foreground/30 text-muted-foreground"
                    )}
                  >
                    {step > s ? <Check className="h-5 w-5" /> : s}
                  </div>
                  <span
                    className={cn(
                      "ml-2 text-sm font-medium",
                      step >= s ? "text-foreground" : "text-muted-foreground"
                    )}
                  >
                    {s === 1 ? "Select Provider" : s === 2 ? "Pick Date & Time" : "Confirm Details"}
                  </span>
                  {s < 3 && (
                    <ChevronRight className="mx-4 h-5 w-5 text-muted-foreground/50" />
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Step 1: Select Provider */}
          {step === 1 && (
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>Choose a Provider</CardTitle>
                  <CardDescription>
                    Select a healthcare provider for your telehealth visit
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {providers.length > 0 ? (
                    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                      {providers.map((provider) => (
                        <Card
                          key={provider.id}
                          className={cn(
                            "cursor-pointer transition-all hover:shadow-md",
                            selectedProvider === provider.id
                              ? "border-primary ring-2 ring-primary/20"
                              : "hover:border-primary/50"
                          )}
                          onClick={() => setSelectedProvider(provider.id)}
                        >
                          <CardContent className="p-4">
                            <div className="flex items-start gap-4">
                              <Avatar className="h-14 w-14">
                                <AvatarImage src={provider.avatar_url || undefined} />
                                <AvatarFallback className="bg-primary/10 text-primary text-lg">
                                  {provider.display_name.split(" ").map((n) => n[0]).join("")}
                                </AvatarFallback>
                              </Avatar>
                              <div className="flex-1">
                                <h3 className="font-semibold text-foreground">{provider.display_name}</h3>
                                <p className="text-sm text-muted-foreground">{provider.specialty}</p>
                                <div className="mt-2 flex items-center gap-2">
                                  <div className="flex items-center gap-1">
                                    <Star className="h-4 w-4 fill-primary text-primary" />
                                    <span className="text-sm font-medium">{provider.rating}</span>
                                  </div>
                                  <span className="text-xs text-muted-foreground">
                                    ({provider.review_count} reviews)
                                  </span>
                                </div>
                                <Badge variant="secondary" className="mt-2">
                                  Available Today
                                </Badge>
                              </div>
                              {selectedProvider === provider.id && (
                                <div className="rounded-full bg-primary p-1">
                                  <Check className="h-4 w-4 text-primary-foreground" />
                                </div>
                              )}
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-12 text-muted-foreground bg-muted/20 rounded-lg border border-dashed">
                      <User className="h-12 w-12 mx-auto mb-4 opacity-50" />
                      <h3 className="font-medium text-lg text-foreground mb-1">No Providers Available</h3>
                      <p>Currently, there are no healthcare providers accepting new appointments.</p>
                      <p className="text-sm mt-2">Please check back later.</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              <div className="flex justify-end">
                <Button
                  onClick={() => setStep(2)}
                  disabled={!canProceedToStep2}
                  className="gap-2"
                >
                  Continue
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}

          {/* Step 2: Select Date & Time */}
          {step === 2 && (
            <div className="space-y-6">
              <div className="grid gap-6 lg:grid-cols-2">
                {/* Calendar */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <CalendarIcon className="h-5 w-5" />
                      Select Date
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Calendar
                      mode="single"
                      selected={selectedDate}
                      onSelect={(date) => {
                        if (date) {
                          setSelectedDate(date);
                          setSelectedTime(null); // Reset time when date changes
                        }
                      }}
                      disabled={(date) => date < new Date() || date > addDays(new Date(), 30)}
                      className="rounded-md border pointer-events-auto"
                    />
                  </CardContent>
                </Card>

                {/* Time Slots */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Clock className="h-5 w-5" />
                      Available Times
                    </CardTitle>
                    <CardDescription>
                      {moment(selectedDate).format("dddd, MMMM D, YYYY")}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {timeSlots.length > 0 ? (
                      <div className="grid grid-cols-3 gap-2">
                        {timeSlots.map((slot) => (
                          <Button
                            key={slot.time}
                            variant={selectedTime?.display === slot.time ? "default" : "outline"}
                            size="sm"
                            disabled={!slot.available}
                            onClick={() => setSelectedTime({ display: slot.time, value: slot.hour24, utcDate: slot.utcDate })}
                            className={cn(
                              "h-10",
                              !slot.available && "opacity-50 cursor-not-allowed"
                            )}
                          >
                            {slot.time}
                          </Button>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <CalendarIcon className="h-10 w-10 mx-auto mb-3 text-muted-foreground opacity-50" />
                        <p className="text-muted-foreground">
                          No available times for this date.
                        </p>
                        <p className="text-sm text-muted-foreground mt-1">
                          Please select another date to see available slots.
                        </p>
                      </div>
                    )}
                    {selectedTime && (
                      <div className="mt-4 rounded-lg bg-primary/5 p-3">
                        <p className="text-sm text-foreground">
                          <span className="font-medium">Selected:</span>{" "}
                          {moment(selectedDate).format("MMM D, YYYY")} at {selectedTime.display}
                        </p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>

              <div className="flex justify-between">
                <Button variant="outline" onClick={() => setStep(1)} className="gap-2">
                  <ChevronLeft className="h-4 w-4" />
                  Back
                </Button>
                <Button
                  onClick={() => setStep(3)}
                  disabled={!canProceedToStep3}
                  className="gap-2"
                >
                  Continue
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}

          {/* Step 3: Confirm Details */}
          {step === 3 && (
            <div className="space-y-6">
              <div className="grid gap-6 lg:grid-cols-2">
                {/* Appointment Summary */}
                <Card>
                  <CardHeader>
                    <CardTitle>Appointment Summary</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex items-center gap-4 rounded-lg border p-4">
                      <Avatar className="h-12 w-12">
                        <AvatarFallback className="bg-primary/10 text-primary">
                          {selectedProviderData?.display_name.split(" ").map((n) => n[0]).join("")}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <h4 className="font-semibold">{selectedProviderData?.display_name}</h4>
                        <p className="text-sm text-muted-foreground">
                          {selectedProviderData?.specialty}
                        </p>
                      </div>
                    </div>

                    <div className="space-y-3">
                      <div className="flex items-center gap-3">
                        <CalendarIcon className="h-5 w-5 text-muted-foreground" />
                        <div>
                          <p className="text-sm text-muted-foreground">Date</p>
                          <p className="font-medium">{moment(selectedDate).format("dddd, MMMM D, YYYY")}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <Clock className="h-5 w-5 text-muted-foreground" />
                        <div>
                          <p className="text-sm text-muted-foreground">Time</p>
                          <p className="font-medium">{selectedTime?.display}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <Video className="h-5 w-5 text-muted-foreground" />
                        <div>
                          <p className="text-sm text-muted-foreground">Visit Type</p>
                          <p className="font-medium">Telehealth Video Visit</p>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Visit Details */}
                <Card>
                  <CardHeader>
                    <CardTitle>Visit Details</CardTitle>
                    <CardDescription>
                      Help your provider prepare for your visit
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <Label className="text-sm font-medium">Reason for Visit *</Label>
                      <RadioGroup
                        value={visitReason}
                        onValueChange={setVisitReason}
                        className="mt-2 grid grid-cols-2 gap-2"
                      >
                        {visitReasons.map((reason) => (
                          <div key={reason} className="flex items-center space-x-2">
                            <RadioGroupItem value={reason} id={reason} />
                            <Label htmlFor={reason} className="text-sm cursor-pointer">
                              {reason}
                            </Label>
                          </div>
                        ))}
                      </RadioGroup>
                    </div>

                    <div>
                      <Label htmlFor="notes" className="text-sm font-medium">
                        Additional Notes (Optional)
                      </Label>
                      <Textarea
                        id="notes"
                        placeholder="Describe any symptoms or concerns you'd like to discuss..."
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        className="mt-2"
                        rows={4}
                      />
                    </div>
                  </CardContent>
                </Card>
              </div>

              <div className="flex justify-between">
                <Button variant="outline" onClick={() => setStep(2)} className="gap-2">
                  <ChevronLeft className="h-4 w-4" />
                  Back
                </Button>
                <Button
                  onClick={() => setShowConfirmDialog(true)}
                  disabled={!canBook}
                  className="gap-2"
                >
                  <Video className="h-4 w-4" />
                  Book Appointment
                </Button>
              </div>
            </div>
          )}
        </main>
      </div>

      {/* Confirmation Dialog */}
      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Your Appointment</DialogTitle>
            <DialogDescription>
              Please review and confirm your telehealth appointment details.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="rounded-lg bg-muted p-4 space-y-2">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Provider</span>
                <span className="font-medium">{selectedProviderData?.display_name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Date</span>
                <span className="font-medium">{moment(selectedDate).format("MMM D, YYYY")}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Time</span>
                <span className="font-medium">{selectedTime?.display}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Reason</span>
                <span className="font-medium">{visitReason}</span>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfirmDialog(false)} disabled={isBooking}>
              Cancel
            </Button>
            <Button onClick={handleBookAppointment} disabled={isBooking}>
              {isBooking ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Booking...
                </>
              ) : (
                "Confirm Booking"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
